import React from 'react';

interface LLMOptimizedStructuredDataProps {
  url: string;
}

export const LLMOptimizedStructuredData = ({ url }: LLMOptimizedStructuredDataProps) => {
  const graphData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://chronocal.io/#organization",
        "name": "ChronoCal",
        "url": "https://chronocal.io",
        "logo": {
          "@type": "ImageObject",
          "url": "https://chronocal.io/assets/icons/branding/chronocal-logo.svg",
          "width": 200,
          "height": 60
        },
        "description": "The world's first performance-based calendar that schedules your work when your brain is wired to win. Integrate your wearables, optimize your energy, and reclaim 15+ hours of peak productivity per week.",
        "foundingDate": "2024",
        "founder": {
          "@type": "Person",
          "name": "Sameer Sontakey",
          "jobTitle": "Founder & CEO",
          "description": "Former Biostrap executive with expertise in wearable technology and chronobiology"
        },
        "sameAs": [
          "https://twitter.com/chronocal",
          "https://linkedin.com/company/chronocal"
        ],
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "customer service",
          "url": "https://chronocal.io/contact",
          "email": "support@chronocal.io"
        },
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "US"
        },
        "knowsAbout": [
          "Chronobiology",
          "Circadian rhythms",
          "Energy management",
          "Productivity optimization",
          "Wearable technology",
          "Calendar scheduling",
          "Deep work",
          "Time management"
        ],
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "ChronoCal Services",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Energy-Aware Scheduling",
                "description": "AI-powered scheduling based on your circadian rhythm and energy patterns"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Wearable Integration",
                "description": "Connect Biostrap, Oura, Whoop, and Ultrahuman devices for accurate energy forecasting"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Deep Work Protection",
                "description": "Automatically block peak focus hours for important work"
              }
            }
          ]
        }
      },
      {
        "@type": "SoftwareApplication",
        "@id": "https://chronocal.io/#software",
        "name": "ChronoCal",
        "applicationCategory": "BusinessApplication",
        "applicationSubCategory": "Productivity Software",
        "operatingSystem": "Web Browser",
        "browserRequirements": "Modern web browser with JavaScript enabled",
        "softwareVersion": "1.0",
        "description": "ChronoCal is a biologically intelligent scheduling platform that helps ambitious founders, executives, and creators align their work with their natural energy patterns. By integrating with wearable devices, ChronoCal provides personalized energy forecasts and automatically suggests optimal meeting times based on your circadian rhythm.",
        "url": "https://chronocal.io",
        "downloadUrl": "https://chronocal.io/signup",
        "installUrl": "https://chronocal.io/signup",
        "screenshot": "https://chronocal.io/assets/images/ui/b5ba0db6-c39f-4016-9785-3b5fe0bbfaaa.png",
        "featureList": [
          "Biologically intelligent scheduling",
          "Wearable device integration (Biostrap, Oura, Whoop, Ultrahuman)",
          "AI-powered energy forecasting",
          "Deep work protection",
          "Circadian rhythm optimization",
          "Calendar integration (Google, Outlook, Apple)",
          "Energy-aware booking pages",
          "Chronotype analysis",
          "Sleep schedule optimization",
          "Time zone handling",
          "Automated scheduling rules",
          "HIPAA compliant data security"
        ],
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD",
          "description": "Free trial available with premium features",
          "availability": "https://schema.org/InStock",
          "validFrom": "2024-01-01",
          "category": "SaaS"
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.8",
          "reviewCount": "500",
          "bestRating": "5",
          "worstRating": "1"
        },
        "publisher": {
          "@id": "https://chronocal.io/#organization"
        },
        "author": {
          "@id": "https://chronocal.io/#organization"
        },
        "keywords": [
          "calendar app",
          "productivity software",
          "energy management",
          "wearable integration",
          "chronobiology",
          "circadian rhythm",
          "deep work",
          "meeting scheduler",
          "time management",
          "productivity tools",
          "Biostrap",
          "Oura",
          "Whoop",
          "Ultrahuman",
          "founder productivity",
          "executive scheduling",
          "peak performance"
        ]
      },
      {
        "@type": "WebPage",
        "@id": "https://chronocal.io/#webpage",
        "url": url,
        "name": "ChronoCal - The Performance Calendar That Works With Your Biology",
        "description": "ChronoCal is the world's first performance-based calendar that schedules your work when your brain is wired to win. Integrate your wearables, optimize your energy, and reclaim 15+ hours of peak productivity per week.",
        "isPartOf": {
          "@type": "WebSite",
          "@id": "https://chronocal.io/#website",
          "url": "https://chronocal.io",
          "name": "ChronoCal",
          "description": "Energy-aware scheduling platform for ambitious founders and creators",
          "publisher": {
            "@id": "https://chronocal.io/#organization"
          },
          "potentialAction": [
            {
              "@type": "SearchAction",
              "target": {
                "@type": "EntryPoint",
                "urlTemplate": "https://chronocal.io/search?q={search_term_string}"
              },
              "query-input": "required name=search_term_string"
            }
          ]
        },
        "about": {
          "@id": "https://chronocal.io/#software"
        },
        "mainEntity": {
          "@id": "https://chronocal.io/#software"
        },
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://chronocal.io"
            }
          ]
        },
        "speakable": {
          "@type": "SpeakableSpecification",
          "cssSelector": ["h1", "h2", "p"]
        }
      }
    ]
  };

  const faqData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Which wearable devices does ChronoCal support?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "ChronoCal integrates with Biostrap, Oura Ring, Whoop, and Ultrahuman. We're continuously adding support for new devices based on user demand."
        }
      },
      {
        "@type": "Question",
        "name": "How long does it take to see results?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Most users report improved energy alignment within the first week. The AI learns your patterns over 7-14 days, with significant productivity improvements typically seen by week 2-3."
        }
      },
      {
        "@type": "Question",
        "name": "Is my health data secure?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Absolutely. ChronoCal is HIPAA compliant with enterprise-grade encryption. Your biometric data is processed locally when possible and never shared with third parties."
        }
      },
      {
        "@type": "Question",
        "name": "Can I use ChronoCal without a wearable device?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes! ChronoCal can learn your patterns through self-reported energy levels and schedule analysis, though wearable integration provides more accurate and automated insights."
        }
      },
      {
        "@type": "Question",
        "name": "Does it work with existing calendar systems?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "ChronoCal integrates seamlessly with Google Calendar, Outlook, Apple Calendar, and most major calendar platforms. It works as an intelligent layer on top of your existing setup."
        }
      }
    ]
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(graphData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqData) }}
      />
    </>
  );
};




